import { Component } from '@theme/component';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages hotspot dialogs.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The desktop popover dialog element.
 * @property {HTMLDialogElement} [mobileSheet] - The mobile bottom sheet dialog.
 * @property {HTMLButtonElement} trigger - The hotspot trigger button.
 * @property {HTMLAnchorElement} [productLink] - The product link element.
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  /** @type {(() => void) | null} */
  #pointerenterHandler = null;
  timer = /** @type {number | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    this.#handleBreakpointChange();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Set up desktop event listeners (hover)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      if (dialog.open) return;

      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  /**
   * Remove desktop event listeners from trigger
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Handle breakpoint changes
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();

    if (this.refs.mobileSheet?.open) {
      this.closeMobileSheet();
    }

    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Calculate the placement of the desktop dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    const containerRect = hotspotsContainer?.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          if (dialogStartY < CONTAINER_GAP) {
            verticalOffset = CONTAINER_GAP - dialogStartY;
          }
          y = 'bottom';
        }
      }
    } else {
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Handle pointer leave on desktop popover.
   * @param {PointerEvent} e - The event.
   * @returns {void}
   */
  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    const isLeavingTrigger = e.target === trigger;
    const isLeavingDialog = e.target === dialog;
    const isGoingToDialog =
      e.relatedTarget === dialog ||
      (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;

    if ((isLeavingTrigger && !isGoingToDialog) || (isLeavingDialog && !isGoingToTrigger)) {
      this.closeDialog();
    }
  };

  /**
   * Get the product link for the hotspot product.
   * @returns {HTMLAnchorElement | null} The product link or null.
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle hotspot click — mobile opens bottom sheet, desktop opens popover.
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    if (isMobileBreakpoint() || isTouchDevice()) {
      e.preventDefault();
      e.stopPropagation();
      this.showMobileSheet();
    } else {
      this.showDialog();
    }
  };

  showDialog = async () => {
    const { dialog } = this.refs;
    await this.#calculateDialogPlacement();
    dialog.dataset.showing = 'true';
    dialog.show();
    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
    dialog.addEventListener('pointerleave', this.#handlePointerLeave);
  };

  /**
   * Close the desktop popover dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog, trigger } = this.refs;
    dialog.dataset.closing = 'true';
    dialog.close();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Open the mobile bottom sheet.
   * @returns {void}
   */
  showMobileSheet = () => {
    const mobileSheet = this.refs.mobileSheet;
    if (!mobileSheet) return;

    document.querySelectorAll('.hotspot-mobile-sheet[open]').forEach((dialog) => {
      if (dialog !== mobileSheet) dialog.close();
    });

    if (this.refs.dialog.open) {
      this.closeDialog();
    }

    const slides = mobileSheet.querySelector('.hotspot-mobile-sheet__slides');
    if (slides instanceof HTMLElement) {
      slides.scrollLeft = 0;
    }

    mobileSheet.showModal();
    document.body.addEventListener('click', this.lightDismissMobileSheet);
    document.body.addEventListener('keydown', this.lightDismissMobileSheetKeyboard);
  };

  /**
   * Close the mobile bottom sheet.
   * @returns {Promise<void>}
   */
  closeMobileSheet = async () => {
    const mobileSheet = this.refs.mobileSheet;
    if (!mobileSheet?.open) return;

    document.body.removeEventListener('click', this.lightDismissMobileSheet);
    document.body.removeEventListener('keydown', this.lightDismissMobileSheetKeyboard);

    mobileSheet.classList.add('dialog-closing');
    await onAnimationEnd(mobileSheet);
    mobileSheet.close();
    mobileSheet.classList.remove('dialog-closing');
  };

  /**
   * Light dismiss the desktop popover.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the desktop popover.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };

  /**
   * Dismiss mobile sheet when tapping the backdrop.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMobileSheet = (event) => {
    const mobileSheet = this.refs.mobileSheet;
    if (mobileSheet && event.target === mobileSheet) {
      this.closeMobileSheet();
    }
  };

  /**
   * Dismiss mobile sheet on Escape.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissMobileSheetKeyboard = (event) => {
    if (event.type === 'keydown' && event.key === 'Escape') {
      this.closeMobileSheet();
    }
  };
}

customElements.define('product-hotspot-component', ProductHotspotComponent);
